import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import PaletteOutlinedIcon from "@material-ui/icons/PaletteOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import { FormControlLabel, Switch } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const TIMEZONES = [
  { value: "America/Sao_Paulo", label: "(UTC-03:00) São Paulo, Brasília" },
  { value: "America/Manaus", label: "(UTC-04:00) Manaus" },
  { value: "America/Rio_Branco", label: "(UTC-05:00) Rio Branco" },
  { value: "America/Noronha", label: "(UTC-02:00) Fernando de Noronha" },
  { value: "UTC", label: "(UTC+00:00) UTC" },
  { value: "America/New_York", label: "(UTC-05:00) New York" },
  { value: "Europe/Lisbon", label: "(UTC+00:00) Lisboa" },
  { value: "Europe/London", label: "(UTC+00:00) Londres" },
  { value: "Europe/Madrid", label: "(UTC+01:00) Madrid" }
];

const LANGUAGES = [
  { value: "pt-BR", label: "Português (Brasil)" },
  { value: "en-US", label: "English (US)" },
  { value: "es-ES", label: "Español" }
];

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/AAAA (06/05/2026)" },
  { value: "MM/DD/YYYY", label: "MM/DD/AAAA (05/06/2026)" },
  { value: "YYYY-MM-DD", label: "AAAA-MM-DD (2026-05-06)" }
];

const CURRENCIES = [
  { value: "BRL", label: "BRL (R$)" },
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" }
];

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.up("md")]: { padding: theme.spacing(4, 6) }
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  legacyPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center"
  },
  legacyOption: {
    marginLeft: "auto",
    minWidth: 180
  },
  cssField: {
    fontFamily: "monospace"
  }
}));

const emptyForm = {
  name: "",
  tradeName: "",
  email: "",
  ownerEmail: "",
  billingEmail: "",
  phone: "",
  mobile: "",
  document: "",
  website: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressDistrict: "",
  addressCity: "",
  addressState: "",
  addressZip: "",
  addressCountry: "BR",
  primaryColor: "#1976d2",
  logoUrl: "",
  timezone: "America/Sao_Paulo",
  language: "pt-BR",
  dateFormat: "DD/MM/YYYY",
  currency: "BRL",
  customCss: ""
};

const Settings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [legacySettings, setLegacySettings] = useState([]);
  const [aiProviders, setAiProviders] = useState([]);
  const [aiForm, setAiForm] = useState({ apiKey: "", defaultModel: "gpt-4o-mini", baseUrl: "" });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/business-settings");
        if (!active) return;
        setForm(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, v == null ? "" : v])
          )
        }));
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setLegacySettings(data);
      } catch (err) {
        // silencioso
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/ai-providers");
        setAiProviders(data || []);
        const openai = (data || []).find(p => p.provider === "openai");
        if (openai?.defaultModel) {
          setAiForm(prev => ({ ...prev, defaultModel: openai.defaultModel }));
        }
        if (openai?.baseUrl) {
          setAiForm(prev => ({ ...prev, baseUrl: openai.baseUrl }));
        }
      } catch (err) {
        // silencioso
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const socket = openSocket();
    socket.on("settings", payload => {
      if (payload.action === "update") {
        setLegacySettings(prev => {
          const aux = [...prev];
          const i = aux.findIndex(s => s.key === payload.setting.key);
          if (i >= 0) aux[i].value = payload.setting.value;
          return aux;
        });
      }
    });
    return () => socket.disconnect();
  }, [isAdmin]);

  const onChange = field => e => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/business-settings", form);
      setForm(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, v == null ? "" : v])
        )
      }));
      toast.success("Configurações salvas.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const getLegacyValue = key => {
    const s = legacySettings.find(x => x.key === key);
    return s ? s.value : "";
  };

  const setLegacyLocal = (key, value) => {
    setLegacySettings(prev => {
      const aux = [...prev];
      const i = aux.findIndex(x => x.key === key);
      if (i >= 0) aux[i] = { ...aux[i], value };
      else aux.push({ key, value });
      return aux;
    });
  };

  const persistLegacy = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      setLegacyLocal(key, value);
      toast.success("Configuração atualizada.");
    } catch (err) {
      toastError(err);
    }
  };

  const openAiInfo = aiProviders.find(p => p.provider === "openai") || {
    provider: "openai",
    configured: false
  };

  const handleSaveOpenAi = async () => {
    setAiSaving(true);
    try {
      const { data } = await api.put("/ai-providers/openai", {
        apiKey: aiForm.apiKey || undefined,
        defaultModel: aiForm.defaultModel || undefined,
        baseUrl: aiForm.baseUrl || undefined
      });
      setAiProviders(data || []);
      setAiForm(prev => ({ ...prev, apiKey: "" }));
      toast.success("Configuracao OpenAI salva.");
    } catch (err) {
      toastError(err);
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestOpenAi = async () => {
    setAiTesting(true);
    try {
      const { data } = await api.post("/ai-providers/openai/test");
      if (data.ok) {
        toast.success(`OpenAI OK. Modelo: ${data.model}`);
      } else {
        toast.error(`Falha: ${data.error}`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setAiTesting(false);
    }
  };

  const handleLegacyChange = async e => {
    try {
      await api.put(`/settings/${e.target.name}`, { value: e.target.value });
      toast.success("Configuração atualizada.");
    } catch (err) {
      toastError(err);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container className={classes.root} maxWidth="md">
      <Typography variant="h5" gutterBottom>
        Configurações do Negócio
      </Typography>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <BusinessOutlinedIcon fontSize="small" /> Dados da Empresa
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome *"
              value={form.name}
              onChange={onChange("name")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome Fantasia"
              placeholder="Opcional"
              value={form.tradeName}
              onChange={onChange("tradeName")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              helperText="Usado em comunicações e emissão de cobrança (quando aplicável)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail *"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail do Responsável"
              placeholder="Opcional"
              type="email"
              value={form.ownerEmail}
              onChange={onChange("ownerEmail")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              helperText="Fallback para integrações de faturamento quando não houver usuário proprietário identificado"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail de Cobrança"
              placeholder="Opcional"
              type="email"
              value={form.billingEmail}
              onChange={onChange("billingEmail")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              helperText="Recebe notificações de cobranças e boletos"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Telefone"
              value={form.phone}
              onChange={onChange("phone")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Celular"
              value={form.mobile}
              onChange={onChange("mobile")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Documento"
              value={form.document}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              helperText="CNPJ/CPF (somente leitura)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Website"
              placeholder="https://..."
              value={form.website}
              onChange={onChange("website")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              label="Endereço"
              placeholder="Rua, avenida..."
              value={form.addressStreet}
              onChange={onChange("addressStreet")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="Nº"
              value={form.addressNumber}
              onChange={onChange("addressNumber")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="Complemento"
              placeholder="Apto, sala..."
              value={form.addressComplement}
              onChange={onChange("addressComplement")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Bairro"
              value={form.addressDistrict}
              onChange={onChange("addressDistrict")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={8} sm={5}>
            <TextField
              label="Cidade"
              value={form.addressCity}
              onChange={onChange("addressCity")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField
              label="UF"
              value={form.addressState}
              onChange={onChange("addressState")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              inputProps={{
                maxLength: 2,
                style: { textTransform: "uppercase" }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              label="CEP"
              placeholder="Somente números"
              value={form.addressZip}
              onChange={onChange("addressZip")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="País"
              value={form.addressCountry}
              onChange={onChange("addressCountry")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              inputProps={{
                maxLength: 2,
                style: { textTransform: "uppercase" }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <PaletteOutlinedIcon fontSize="small" /> Branding e Preferências
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cor Primária"
              type="color"
              value={form.primaryColor || "#1976d2"}
              onChange={onChange("primaryColor")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              helperText="Cor usada no tema/branding"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Logo (URL)"
              placeholder="https://..."
              value={form.logoUrl}
              onChange={onChange("logoUrl")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
              helperText="URL do logo (opcional)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Fuso Horário *"
              value={form.timezone}
              onChange={onChange("timezone")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            >
              {TIMEZONES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Idioma *"
              value={form.language}
              onChange={onChange("language")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            >
              {LANGUAGES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Formato de Data *"
              value={form.dateFormat}
              onChange={onChange("dateFormat")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            >
              {DATE_FORMATS.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Moeda *"
              value={form.currency}
              onChange={onChange("currency")}
              fullWidth
              variant="outlined"
              size="small"
              disabled={!isAdmin}
            >
              {CURRENCIES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="CSS Customizado"
              placeholder="/* Opcional: CSS aplicado ao sistema */"
              value={form.customCss}
              onChange={onChange("customCss")}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={4}
              disabled={!isAdmin}
              InputProps={{ classes: { input: classes.cssField } }}
              helperText="Use com cuidado. CSS inválido pode afetar a interface."
            />
          </Grid>
        </Grid>

        {isAdmin && (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        )}
      </Paper>

      {isAdmin && (
        <Paper className={classes.paper}>
          <Typography className={classes.sectionTitle}>
            <MemoryOutlinedIcon fontSize="small" /> IA / Provedores
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: "block", marginBottom: 12 }}
          >
            A chave da API e armazenada criptografada (AES-256-GCM) e nunca
            retorna em plain text para o frontend.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                <strong>OpenAI:</strong>{" "}
                {openAiInfo.configured ? (
                  <span style={{ color: "#2e7d32" }}>
                    Configurada ({openAiInfo.apiKeyHint})
                  </span>
                ) : openAiInfo.unreadable ? (
                  <span style={{ color: "#b26a00" }}>
                    Gravada, mas ilegível
                  </span>
                ) : (
                  <span style={{ color: "#c62828" }}>Nao configurada</span>
                )}
              </Typography>
              {/* "Não configurada" e "a chave de criptografia mudou" levavam à
                  mesma mensagem. Quem redigitava sem saber disso repetia o
                  problema no próximo giro do JWT_SECRET. */}
              {openAiInfo.unreadable && (
                <Typography variant="caption" style={{ color: "#b26a00" }}>
                  Existe uma configuração salva que não abre com a chave de
                  criptografia atual — ela mudou desde que foi gravada. Sem
                  PAYMENTS_ENC_KEY no .env, a chave deriva do JWT_SECRET, e
                  girá-lo invalida o que já estava cifrado. Informe a chave da
                  OpenAI novamente para regravar.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} style={{ textAlign: "right" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleTestOpenAi}
                disabled={!openAiInfo.configured || aiTesting}
              >
                {aiTesting ? "Testando..." : "Testar conexao"}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API Key OpenAI"
                placeholder={
                  openAiInfo.configured ? "Deixe em branco p/ manter atual" : "sk-..."
                }
                type="password"
                value={aiForm.apiKey}
                onChange={e => {
                  const value = e.target.value;
                  setAiForm(prev => ({ ...prev, apiKey: value }));
                }}
                fullWidth
                variant="outlined"
                size="small"
                helperText="Use uma chave dedicada para o ambiente. Comeca com sk-."
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Modelo padrao"
                value={aiForm.defaultModel}
                onChange={e => {
                  const value = e.target.value;
                  setAiForm(prev => ({ ...prev, defaultModel: value }));
                }}
                fullWidth
                variant="outlined"
                size="small"
                helperText="ex.: gpt-4o-mini, gpt-4o"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Base URL (opcional)"
                placeholder="https://api.openai.com/v1"
                value={aiForm.baseUrl}
                onChange={e => {
                  const value = e.target.value;
                  setAiForm(prev => ({ ...prev, baseUrl: value }));
                }}
                fullWidth
                variant="outlined"
                size="small"
                helperText="Proxies compativeis com OpenAI"
              />
            </Grid>
          </Grid>
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveOpenAi}
              disabled={aiSaving}
            >
              {aiSaving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Paper>
      )}

      {isAdmin && (
        <Paper className={classes.paper}>
          <Typography className={classes.sectionTitle}>
            <ForumOutlinedIcon fontSize="small" /> Mensagens do Atendimento
          </Typography>

          <Paper variant="outlined" className={classes.legacyPaper}>
            <FormControlLabel
              control={
                <Switch
                  checked={getLegacyValue("assignmentMessageEnabled") === "enabled"}
                  onChange={e =>
                    persistLegacy(
                      "assignmentMessageEnabled",
                      e.target.checked ? "enabled" : "disabled"
                    )
                  }
                  color="primary"
                />
              }
              label="Enviar mensagem ao atribuir atendimento"
            />
            <TextField
              label="Mensagem de atribuição"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              helperText="Enviada automaticamente ao atribuir um atendente ao ticket. Variáveis disponíveis: {{name}}, {{ms}}, {{hour}}."
              value={getLegacyValue("assignmentMessage") || ""}
              onChange={e => setLegacyLocal("assignmentMessage", e.target.value)}
              onBlur={e => persistLegacy("assignmentMessage", e.target.value)}
              style={{ marginTop: 12 }}
            />
          </Paper>

          <Paper variant="outlined" className={classes.legacyPaper}>
            <FormControlLabel
              control={
                <Switch
                  checked={getLegacyValue("closingMessageEnabled") === "enabled"}
                  onChange={e =>
                    persistLegacy(
                      "closingMessageEnabled",
                      e.target.checked ? "enabled" : "disabled"
                    )
                  }
                  color="primary"
                />
              }
              label="Enviar mensagem ao encerrar atendimento"
            />
            <TextField
              label="Mensagem de encerramento"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              helperText="Mensagem padrão sugerida ao resolver um ticket. Variáveis disponíveis: {{name}}, {{ms}}, {{hour}}."
              value={getLegacyValue("closingMessage") || ""}
              onChange={e => setLegacyLocal("closingMessage", e.target.value)}
              onBlur={e => persistLegacy("closingMessage", e.target.value)}
              style={{ marginTop: 12 }}
            />
          </Paper>

          <Paper variant="outlined" className={classes.legacyPaper}>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    (getLegacyValue("reopenTicketEnabled") || "enabled") ===
                    "enabled"
                  }
                  onChange={e =>
                    persistLegacy(
                      "reopenTicketEnabled",
                      e.target.checked ? "enabled" : "disabled"
                    )
                  }
                  color="primary"
                />
              }
              label="Reabrir ticket fechado ao receber nova mensagem"
            />
            <Typography variant="caption" color="textSecondary">
              Quando habilitado, novas mensagens de um contato com ticket
              encerrado reabrem o mesmo ticket. Quando desabilitado, apenas
              tickets fechados nas últimas 2 horas são reabertos; após esse
              período um novo ticket é criado.
            </Typography>
          </Paper>
        </Paper>
      )}

      {isAdmin && legacySettings.length > 0 && (
        <Paper className={classes.paper}>
          <Typography className={classes.sectionTitle}>
            <SettingsOutlinedIcon fontSize="small" /> Outras Configurações
          </Typography>
          <Paper variant="outlined" className={classes.legacyPaper}>
            <Typography variant="body1">Cadastro de usuários</Typography>
            <TextField
              select
              size="small"
              variant="outlined"
              name="userCreation"
              value={getLegacyValue("userCreation") || "enabled"}
              onChange={handleLegacyChange}
              className={classes.legacyOption}
            >
              <MenuItem value="enabled">Habilitado</MenuItem>
              <MenuItem value="disabled">Desabilitado</MenuItem>
            </TextField>
          </Paper>
          <Paper variant="outlined" className={classes.legacyPaper}>
            <TextField
              label="Token API"
              size="small"
              variant="outlined"
              fullWidth
              value={getLegacyValue("userApiToken") || ""}
              InputProps={{ readOnly: true }}
            />
          </Paper>
        </Paper>
      )}
    </Container>
  );
};

export default Settings;
